import React, { useEffect, useMemo, useRef, useState } from "react";

/** Smoothly counts from 0→target over `duration` ms. Pure CSS wouldn't
 *  let us format large numbers with localised separators, so we run a
 *  manual rAF loop and format on every frame. */
export function CountUp({ value = 0, duration = 900, format = (n) => n.toLocaleString() }) {
    const [display, setDisplay] = useState(0);
    const prev = useRef(0);
    useEffect(() => {
        const start = performance.now();
        const from = prev.current;
        const to = Number(value) || 0;
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // ease-out cubic — decelerates quickly which feels punchy for dashboards
            const eased = 1 - Math.pow(1 - t, 3);
            const v = from + (to - from) * eased;
            setDisplay(v);
            if (t < 1) raf = requestAnimationFrame(tick);
            else prev.current = to;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [value, duration]);
    return <>{format(Math.round(display))}</>;
}

export function formatBytes(bytes) {
    if (!bytes || bytes < 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    let v = bytes;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

export function timeAgo(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (!t) return "—";
    const s = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
}

/** Glassy card container used across the dashboard. */
export function SACard({ className = "", children, ...rest }) {
    return (
        <div
            className={
                "rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 shadow-[0_12px_40px_-20px_rgba(0,0,0,0.9)] backdrop-blur-sm transition-colors hover:border-white/[0.1] " +
                className
            }
            {...rest}
        >
            {children}
        </div>
    );
}

export function MetricCard({ icon: Icon, label, value, sublabel, trend, tone = "default", testId }) {
    const toneClass = {
        default: "text-[#00C2FF]",
        good: "text-emerald-300",
        warn: "text-amber-300",
        bad: "text-red-300",
    }[tone];
    return (
        <SACard data-testid={testId} className="flex flex-col justify-between">
            <div className="flex items-center gap-2">
                {Icon && <Icon size={14} className={toneClass} />}
                <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                    {label}
                </div>
            </div>
            <div className="mt-3 font-semibold leading-none text-white" style={{ fontSize: 28 }}>
                {typeof value === "number" ? <CountUp value={value} /> : value}
            </div>
            {(sublabel || trend != null) && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-white/55">
                    {sublabel && <span>{sublabel}</span>}
                    {trend != null && (
                        <span
                            className={[
                                "rounded-sm px-1 py-px text-[10px] font-semibold",
                                trend >= 0
                                    ? "bg-emerald-400/10 text-emerald-300"
                                    : "bg-red-400/10 text-red-300",
                            ].join(" ")}
                        >
                            {trend >= 0 ? "+" : ""}
                            {trend}%
                        </span>
                    )}
                </div>
            )}
        </SACard>
    );
}

export function SeverityDot({ severity }) {
    const color = {
        critical: "#f87171",
        warning: "#fbbf24",
        info: "#38bdf8",
    }[severity] || "#cbd5e1";
    return (
        <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: color, boxShadow: `0 0 8px ${color}` }}
        />
    );
}

export function StatusPill({ status }) {
    const map = {
        active: { label: "ACTIVE", color: "#34d399" },
        suspended: { label: "SUSPENDED", color: "#f87171" },
        deleted: { label: "DELETED", color: "#a78bfa" },
        purged: { label: "PURGED", color: "#64748b" },
    };
    const { label, color } = map[status] || { label: (status || "?").toUpperCase(), color: "#94a3b8" };
    return (
        <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
            style={{
                borderColor: `${color}55`,
                background: `${color}14`,
                color,
            }}
        >
            <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: color, boxShadow: `0 0 6px ${color}` }}
            />
            {label}
        </span>
    );
}

/** Circular progress used for the Platform Health score. */
export function HealthRing({ score = 0, label = "Healthy" }) {
    const size = 148;
    const r = 60;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(100, score));
    const color = pct >= 85 ? "#34d399" : pct >= 60 ? "#fbbf24" : "#f87171";
    return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth="10"
                    fill="transparent"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke={color}
                    strokeWidth="10"
                    fill="transparent"
                    strokeLinecap="round"
                    strokeDasharray={c}
                    strokeDashoffset={c - (c * pct) / 100}
                    style={{
                        transform: "rotate(-90deg)",
                        transformOrigin: "50% 50%",
                        transition: "stroke-dashoffset 900ms ease-out",
                    }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-[34px] font-semibold leading-none">
                    <CountUp value={pct} />
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-white/55">/ 100</div>
                <div className="mt-1.5 text-[11px]" style={{ color }}>
                    {label}
                </div>
            </div>
        </div>
    );
}

/** Lightweight inline SVG line chart — no recharts here so the top metrics
 *  row renders instantly. Accepts `series=[{values:[...], color, label}]`. */
export function Sparkline({ values = [], color = "#00C2FF", height = 36, fill = true }) {
    const { path, area } = useMemo(() => {
        if (!values.length) return { path: "", area: "" };
        const min = Math.min(...values);
        const max = Math.max(...values);
        const span = max - min || 1;
        const w = 100;
        const h = height;
        const step = w / Math.max(1, values.length - 1);
        const pts = values.map((v, i) => [i * step, h - ((v - min) / span) * (h - 4) - 2]);
        const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(" ");
        const a = `${d} L${(pts.length ? pts[pts.length - 1][0] : 0).toFixed(2)},${h} L0,${h} Z`;
        return { path: d, area: a };
    }, [values, height]);
    return (
        <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
            {fill && <path d={area} fill={color} opacity="0.12" />}
            <path d={path} stroke={color} strokeWidth="1.5" fill="none" />
        </svg>
    );
}
